import React, { PureComponent } from 'react';
import {
  WithStyles,
  createStyles,
  withStyles,
} from '@lifeomic/chroma-react/styles';
import Survey, { FieldValueUpdate } from '../util/Survey';
import {
  FieldUpdateContext,
  SurveyContext,
  SurveyResponseContext,
  IsEnabledContext,
  IsReadOnlyContext,
  AutoSaveContext,
  SubjectIdContext,
  RepeatingGroupActionsContext,
  RepeatingGroupActions,
} from './Contexts';
import SurveyForm from './SurveyForm';
import PaginatedSurveyForm from './PaginatedSurveyForm';
import SurveyResponse from '../util/SurveyResponse';
import { DialogText } from './SubmitButton';
import SurveyHeader from './SurveyHeader';
import clsx from 'clsx';
import { SurveyOverrideClasses } from './SurveyOverrideClasses';
import { getTestProps } from '../../../util/testProps';
import SurveyItemRefsProvider from './SurveyItemRefsProvider';
import WarnOnRedirect from '../../WarnOnRedirect';
import { debounce } from 'lodash';
import moment from 'moment';
import { useActiveAccountId } from '../../../hooks/useActiveAccountId';
import { v4 } from 'uuid';
import {
  canViewTemplatePreview,
  PreviewVisibility,
  SurveyTemplatePreview,
} from './SurveyTemplatePreview';
import { evaluatedPolicySelectors } from '../../../redux/modules/evaluatedPolicy';
import { useSelector } from 'react-redux';
import { Policy } from '../../../util/abac';
import { resolveSurveyFillSubjectId } from './FillFromChart/useSurveyFill';

export const testIds = {
  surveyRoot: 'survey-surveyRoot',
  surveyContentRoot: 'survey-surveyContentRoot',
};

const styles = () =>
  createStyles({
    root: {
      width: '100%',
      display: 'flex',
      justifyContent: 'center',
      flexDirection: 'row',
      alignItems: 'flex-start',
      height: '100%',
      maxWidth: 1200,
      margin: '0 auto',
      gap: 16,
    },
    // Survey-level compact-layout extension: cap the form to a centered,
    // tablet-friendly column. The existing `margin: 0 auto` centers it.
    compactRoot: {
      maxWidth: 840,
    },
    // While the template preview is expanded, the form-only caps (1200/840)
    // would squeeze the form to half of that, so widen the row. Defined after
    // compactRoot so it wins for compact-layout surveys too. `flexWrap` lets
    // the preview drop below the form on narrow windows (see the flex-basis
    // values on the two panes) instead of overflowing horizontally.
    previewExpandedRoot: {
      maxWidth: 1600,
      flexWrap: 'wrap',
    },
    contentRoot: {
      display: 'flex',
      flexDirection: 'column',
      flex: 1,
      width: '100%',
      height: '100%',
    },
    // Beside an expanded preview the form takes priority (3:2) and never
    // shrinks below a usable width. When 600px + the preview's 400px basis
    // no longer fit, the preview wraps under the form.
    previewExpandedContentRoot: {
      flex: '3 1 600px',
      minWidth: 600,
    },
  });

export interface SurveyComponentOwnProps {
  dialogText?: DialogText;
  secondaryTitle?: string;
  isReadOnly: boolean;
  questionnaireResponse: fhir.QuestionnaireResponse;
  questionnaire: fhir.Questionnaire;
  onResponseUpdate: (
    updatedQuestionnaireRespone: any,
    isValidFhirResource: boolean,
  ) => Promise<void> | void;
  onSaveDraft?: (
    updatedQuestionnaireResponse: fhir.QuestionnaireResponse,
  ) => void;
  onComplete: (isAutoSave?: boolean, onSaveComplete?: () => void) => void;
  defaultStartPageLink?: string;
  onClose?: () => void;
  nonSubjectAuthor?: { reference?: string; display?: string };
  overrideClasses?: SurveyOverrideClasses;
  className?: string;
  autopopulateValues?: FieldValueUpdate[];
  subjectId?: string;
  autoSave?: boolean;
  activeAccount?: string;
  policy?: Policy;
  // Survey option AllowsResponseResubmission can allow further edits to a completed response
  // but we also want to control which context this is permitted in
  // which is the purpose of this prop
  useAllowsResponseResubmission?: boolean;
  templatePreviewEnabled?: boolean;
}

export type SurveyComponentProps = SurveyComponentOwnProps &
  WithStyles<typeof styles>;

export interface SurveyComponentState {
  survey: Survey;
  currentPageLink: string;
  autosaveTimestamp?: string;
  surveyInReadOnlyMode: boolean;
  /**
   * Incremented whenever a repeating group instance is added or removed.
   * The active form reseeds Formik from the updated response (indices shift
   * on removal) without remounting the page, so collapsible sections and
   * scroll position stay put.
   */
  instanceRevision: number;
  /**
   * Mirror of the template preview's visibility (source of truth lives in
   * SurveyTemplatePreview via localStorage) so the layout can widen while
   * the preview is expanded.
   */
  templatePreviewVisibility: PreviewVisibility;
}

const defaultOverride = {} as SurveyOverrideClasses;

export const buildDefaultQuestionnaireResponse = (
  questionnaire: fhir.Questionnaire,
  nonSubjectAuthor?: { reference: string; display: string },
  autopopulateValues: FieldValueUpdate[] = [],
): fhir.QuestionnaireResponse => {
  const asSurvey = new Survey(questionnaire);
  let defaultResponse = asSurvey.defaultResponseWithInitialValues(
    null,
    asSurvey.allRepeatingItems,
    autopopulateValues,
  );

  if (nonSubjectAuthor) {
    defaultResponse = defaultResponse.setAuthor(nonSubjectAuthor);
  }
  return defaultResponse.toFhirQuestionnaireResponse();
};

/**
 * New survey opens stamp a client UUID before survey-service create. Autosave is
 * debounced and field-driven, so Fill can race a draft that does not exist on
 * the server yet. Eager-persist once when autoSave is on and meta.versionId is
 * still missing.
 */
export const needsEagerSurveyResponsePersist = ({
  autoSave,
  isReadOnly,
  responseId,
  versionId,
}: {
  autoSave?: boolean;
  isReadOnly?: boolean;
  responseId?: string | null;
  versionId?: string | null;
}): boolean =>
  Boolean(autoSave && !isReadOnly && responseId?.trim() && !versionId?.trim());

class SurveyComponent extends PureComponent<
  SurveyComponentProps,
  SurveyComponentState
> {
  state: SurveyComponentState;
  // A default id to use when saving the response
  defaultResponseId = v4();
  private fillGeneration = 0;
  private pendingFilledResponse: fhir.QuestionnaireResponse | undefined;
  private didRequestEagerPersist = false;

  constructor(props: SurveyComponentProps) {
    super(props);

    const {
      questionnaire,
      isReadOnly,
      defaultStartPageLink,
      useAllowsResponseResubmission,
    } = props;

    const survey = new Survey(questionnaire);

    const surveyResponse = this._getSurveyResponse(survey);
    if (!this.props.questionnaireResponse) {
      // this was just initialized, update parent
      this._safeUpdateResponse(surveyResponse, true);
    }

    const surveyInReadOnlyMode =
      isReadOnly ||
      (surveyResponse.isComplete &&
        !(survey.allowsResponseResubmission && useAllowsResponseResubmission));

    let currentPageLink = null;

    if (defaultStartPageLink) {
      // default start page can be explictely defined.  useful for survey builder preview
      // @ts-ignore This directive was added as part of a bulk-change to enable strictNullChecks.
      currentPageLink = defaultStartPageLink;
    } else if (
      survey.isPaginatedSurvey &&
      surveyResponse.lastVisitedPage &&
      !surveyInReadOnlyMode
    ) {
      // resume where the user last left off if this is a paginated survey and not in read only mode
      // @ts-ignore This directive was added as part of a bulk-change to enable strictNullChecks.
      currentPageLink = surveyResponse.lastVisitedPage;
    } else if (survey.isPaginatedSurvey) {
      // there is no "last visited page" on the response, default it
      // @ts-ignore This directive was added as part of a bulk-change to enable strictNullChecks.
      currentPageLink = survey.getDefaultSurveyPagination().curLink || null;
    }

    if (!isReadOnly && surveyResponse.isComplete) {
      console.error(
        'Survey set as editable, but survey response is complete.  Component will force read only mode',
      );
    }

    this.state = {
      survey,
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      currentPageLink,
      surveyInReadOnlyMode,
      instanceRevision: 0,
      templatePreviewVisibility: 'collapsed',
    };
  }

  componentDidMount() {
    this.requestEagerPersistIfNeeded();
  }

  componentDidUpdate() {
    this.requestEagerPersistIfNeeded();
  }

  requestEagerPersistIfNeeded = () => {
    if (this.didRequestEagerPersist) {
      return;
    }

    const propsResponse = this.props.questionnaireResponse;
    const surveyResponse = this._getSurveyResponse();
    const responseId = propsResponse?.id ?? surveyResponse.id;
    const versionId =
      propsResponse?.meta?.versionId ??
      surveyResponse.responseWithoutItem?.meta?.versionId;

    if (
      !needsEagerSurveyResponsePersist({
        autoSave: this.props.autoSave,
        isReadOnly: this.props.isReadOnly,
        responseId,
        versionId,
      })
    ) {
      return;
    }

    this.didRequestEagerPersist = true;
    this.handleSaveProgress(true);
  };

  /**
   * The input to this component is a fhir questionniareResponse, but the implementation
   * uses a wrapper SurveyResponse object.  This generates that wrapper
   * @memberof SurveyComponent
   */
  _getSurveyResponse = (survey: Survey = this.state.survey): SurveyResponse => {
    const questionnaireResponse = this.props.questionnaireResponse;
    // if the response has not yet been started, be sure to initialize a response with default value
    const responseStarted = !SurveyResponse.isQuestionnaireResponseNotStarted(
      questionnaireResponse,
    );

    if (responseStarted) {
      return SurveyResponse.initFromFhirQuestionnaireResponse(
        questionnaireResponse,
        survey.allRepeatingItems,
        survey.allRepeatingGroupItems,
      );
    } else {
      let defaultSurvey = survey.defaultResponseWithInitialValues(
        questionnaireResponse,
        survey.allRepeatingItems,
        this.props.autopopulateValues,
      );

      if (this.props.nonSubjectAuthor) {
        defaultSurvey = defaultSurvey.setAuthor(this.props.nonSubjectAuthor);
      }
      return defaultSurvey;
    }
  };

  _handlePageChange = (isBack: boolean) => {
    const { survey, currentPageLink } = this.state;

    if (!survey.isPaginatedSurvey) {
      throw new Error(
        'Attempted a pagination operation on a non-paginated survey',
      );
    }

    const surveyResponse = this._getSurveyResponse();
    const pagination = survey.getSurveyPaginationForLink(
      currentPageLink,
      surveyResponse,
    );
    const newPageLink = isBack ? pagination.prevLink : pagination.nextLink;

    /*
      if this is a paginated survey, update the response with the last visited page.
      This allows user to resume where they left off in case they close the survey
    */
    const updatedSurveyResponse =
      surveyResponse.updateLastVisitedPage(newPageLink);

    // any page change should require a valid form
    this._safeUpdateResponse(updatedSurveyResponse, true);

    this.setState({
      currentPageLink: newPageLink,
    });
  };

  _safeUpdateResponse = (
    response: SurveyResponse,
    isValidFhirResource: boolean,
  ) => {
    if (this.props.isReadOnly) return;

    if (!this.props.onResponseUpdate) {
      throw new Error(
        '"onResponseUpdate" handler required for a non-read only survey',
      );
    }

    // If the response does not have an id, use the default id to ensure
    // the response isn't saved twice with different ids
    if (!response.id && response.responseWithoutItem) {
      response.responseWithoutItem.id = this.defaultResponseId;
    }

    return this.props.onResponseUpdate(
      response.toFhirQuestionnaireResponse(),
      isValidFhirResource,
    );
  };

  handleNextSurveyPage = () => {
    this._handlePageChange(false);
  };

  handleSaveProgress = (isAutoSave?: boolean) => {
    if (!isAutoSave) {
      // A new save is requested by the user, cancel any pending auto save
      this.autoSaveResponse.cancel();
    }

    const generation = this.fillGeneration;
    const revisionAtStart = this.state.instanceRevision;
    const updatedResponse = this._getSurveyResponse().setAsInProgress();

    Promise.resolve().then(async () => {
      if (generation !== this.fillGeneration) {
        return;
      }
      await this._safeUpdateResponse(updatedResponse, true);
      if (generation !== this.fillGeneration) {
        await this.recoverAfterStaleFillWrite(generation, revisionAtStart);
        return;
      }

      this.props.onComplete?.(isAutoSave, () => {
        this.setState({
          autosaveTimestamp: moment().format('hh:mm:ss A'),
        });
      });
    });
  };

  handleCompletion = () => {
    Promise.resolve().then(async () => {
      const updatedResponse = this._getSurveyResponse().setAsComplete();

      await this._safeUpdateResponse(updatedResponse, true);

      this.props.onComplete?.();
    });
  };

  handleSaveDraft = () => {
    if (this.props.onSaveDraft) {
      const updatedResponse = this._getSurveyResponse();
      this.props.onSaveDraft(updatedResponse.toFhirQuestionnaireResponse());
    }
  };

  handlePrevSurveyPage = () => {
    this._handlePageChange(true);
  };

  autoSaveResponse = debounce(() => {
    if (this.props.autoSave && !this.props.isReadOnly) {
      this.handleSaveProgress(true);
    }
  }, 3000);

  handleAddRepeatingGroupInstance = async (
    groupPath: string[],
    displayedInstanceCount?: number,
  ) => {
    let updatedResponse = this._getSurveyResponse();
    // the renderer may display placeholder cards padded to minOccurs beyond
    // the answered count; materialize instances up past the displayed count
    // so the add is visible immediately instead of filling the padding gap
    const targetCount =
      Math.max(
        updatedResponse.getRepeatingGroupInstanceCount(groupPath),
        displayedInstanceCount ?? 0,
      ) + 1;

    while (
      updatedResponse.getRepeatingGroupInstanceCount(groupPath) < targetCount
    ) {
      updatedResponse = updatedResponse.addRepeatingGroupInstance(groupPath);
    }

    await this._safeUpdateResponse(updatedResponse, false);
    this.setState(state => ({ instanceRevision: state.instanceRevision + 1 }));
    this.autoSaveResponse();
  };

  handleRemoveRepeatingGroupInstance = async (
    groupPath: string[],
    instanceIndex: number,
  ) => {
    const updatedResponse =
      this._getSurveyResponse().removeRepeatingGroupInstance(
        groupPath,
        instanceIndex,
      );

    await this._safeUpdateResponse(updatedResponse, false);
    this.setState(state => ({ instanceRevision: state.instanceRevision + 1 }));
    this.autoSaveResponse();
  };

  handleFilledResponse = (
    questionnaireResponse: fhir.QuestionnaireResponse,
  ) => {
    this.fillGeneration += 1;
    this.pendingFilledResponse = questionnaireResponse;
    this.autoSaveResponse.cancel();
    void Promise.resolve(
      this.props.onResponseUpdate(questionnaireResponse, true),
    ).then(() => {
      this.setState(state => ({
        instanceRevision: state.instanceRevision + 1,
      }));
    });
  };

  reapplyFilledResponse = async () => {
    if (!this.pendingFilledResponse) {
      return;
    }
    await Promise.resolve(
      this.props.onResponseUpdate(this.pendingFilledResponse, true),
    );
    this.setState(state => ({
      instanceRevision: state.instanceRevision + 1,
    }));
  };

  recoverAfterStaleFillWrite = async (
    generationAtStart: number,
    revisionAtStart: number,
  ) => {
    if (generationAtStart === this.fillGeneration) {
      return;
    }
    // Formik already reseeded from fill and may include answers typed after
    // that apply. Persist the live form instead of the frozen fill snapshot.
    if (this.state.instanceRevision !== revisionAtStart) {
      await this._safeUpdateResponse(
        this._getSurveyResponse().setAsInProgress(),
        true,
      );
      return;
    }
    await this.reapplyFilledResponse();
  };

  repeatingGroupActions: RepeatingGroupActions = {
    onAddInstance: this.handleAddRepeatingGroupInstance,
    onRemoveInstance: this.handleRemoveRepeatingGroupInstance,
  };

  handleTemplatePreviewVisibilityChange = (visibility: PreviewVisibility) => {
    this.setState({ templatePreviewVisibility: visibility });
  };

  handleFieldUpdate = async (fieldUpdates: FieldValueUpdate[]) => {
    const generation = this.fillGeneration;
    const revisionAtStart = this.state.instanceRevision;
    const derivedUpdates = this.state.survey.calculatedFieldUpdates(
      this._getSurveyResponse(),
      fieldUpdates,
    );
    const allFieldUpdates = derivedUpdates.length
      ? [...fieldUpdates, ...derivedUpdates]
      : fieldUpdates;
    const responseUpdates =
      this.state.survey.fieldUpdatesToResponseUpdates(allFieldUpdates);
    const modifiedResponses = responseUpdates.filter(
      update =>
        this._getSurveyResponse().getValueAtPath(update.updatePath) !==
        update.value,
    );
    const updatedResponse =
      this._getSurveyResponse().updateFields(responseUpdates);

    if (generation !== this.fillGeneration) {
      return;
    }

    /*
      This is triggered for all updates.  This update may make a form (and questionnaireResponse) invalid.
      Assume that this is not a valid form and enhance in the future to check for validity on each update.
    */
    await this._safeUpdateResponse(updatedResponse, false);
    if (generation !== this.fillGeneration) {
      await this.recoverAfterStaleFillWrite(generation, revisionAtStart);
      return;
    }

    if (modifiedResponses.length > 0) {
      this.autoSaveResponse();
    }
    // If the response has not been saved, attachments will not be able
    // to be uploaded because they require the response id to exist. This
    // will flush the auto save to ensure the response is saved immediately
    // so the attachment field can update the value and create the attachment.
    if (
      !updatedResponse.id &&
      modifiedResponses.some(u => u.valueType === 'valueAttachment')
    ) {
      this.autoSaveResponse.flush();
    }
  };

  render() {
    const {
      classes,
      dialogText,
      secondaryTitle,
      className,
      overrideClasses = defaultOverride,
      onSaveDraft,
      templatePreviewEnabled,
      policy,
    } = this.props;

    const { survey, currentPageLink, surveyInReadOnlyMode } = this.state;

    const surveyResponse = this._getSurveyResponse();
    const surveyPagination = survey.getSurveyPaginationForLink(
      currentPageLink,
      surveyResponse,
    );

    const currentPageSurveyItem = survey.getSurveyPageWithLink(currentPageLink);

    const showsTemplatePreview = Boolean(
      templatePreviewEnabled &&
        survey.showsTemplatePreview &&
        policy &&
        canViewTemplatePreview(policy),
    );
    const templatePreviewExpanded =
      showsTemplatePreview &&
      this.state.templatePreviewVisibility === 'expanded';

    return (
      <div
        className={clsx(
          classes.root,
          survey.hasCompactLayout() && classes.compactRoot,
          templatePreviewExpanded && classes.previewExpandedRoot,
          className,
        )}
        {...getTestProps(testIds.surveyRoot)}
      >
        {/* Make sure a user doesn't navigate to a markdown link without warning */}
        <WarnOnRedirect />
        <div
          className={clsx(
            classes.contentRoot,
            templatePreviewExpanded && classes.previewExpandedContentRoot,
            overrideClasses.surveyContainer,
          )}
          {...getTestProps(testIds.surveyContentRoot)}
        >
          <SurveyHeader
            survey={survey}
            currentPageLink={currentPageLink}
            secondaryTitle={secondaryTitle}
          />
          <SurveyContext.Provider value={this.state.survey}>
            <SurveyItemRefsProvider>
              <FieldUpdateContext.Provider value={this.handleFieldUpdate}>
                <RepeatingGroupActionsContext.Provider
                  value={this.repeatingGroupActions}
                >
                  <SurveyResponseContext.Provider value={surveyResponse}>
                    <IsEnabledContext.Provider value={true}>
                      {/* Completed surveys force read only */}
                      <IsReadOnlyContext.Provider value={surveyInReadOnlyMode}>
                        <AutoSaveContext.Provider
                          value={Boolean(this.props.autoSave)}
                        >
                          <SubjectIdContext.Provider
                            value={
                              resolveSurveyFillSubjectId(
                                this.props.subjectId,
                                surveyResponse.responseWithoutItem,
                              ) ?? ''
                            }
                          >
                            {
                              survey.isPaginatedSurvey ? (
                                <PaginatedSurveyForm
                                  overrideClasses={overrideClasses}
                                  dialogText={dialogText}
                                  key={currentPageLink}
                                  formikReseedRevision={
                                    this.state.instanceRevision
                                  }
                                  onFilledResponse={this.handleFilledResponse}
                                  onNextPage={this.handleNextSurveyPage}
                                  hasNextPage={surveyPagination.hasNext}
                                  onPrevPage={this.handlePrevSurveyPage}
                                  hasPrevPage={surveyPagination.hasPrev}
                                  prevPageDisabled={surveyPagination.getPrevPageDisabled(
                                    surveyInReadOnlyMode,
                                  )}
                                  enforcePageCheckpoint={surveyPagination.shouldEnforcePageAsCheckpoint(
                                    surveyInReadOnlyMode,
                                  )}
                                  surveyItem={currentPageSurveyItem}
                                  onCompletion={this.handleCompletion}
                                  onSaveProgress={this.handleSaveProgress}
                                  onSaveDraft={onSaveDraft}
                                  autosaveTimestamp={
                                    this.state.autosaveTimestamp
                                  }
                                />
                              ) : (
                                <SurveyForm
                                  overrideClasses={overrideClasses}
                                  formikReseedRevision={
                                    this.state.instanceRevision
                                  }
                                  onFilledResponse={this.handleFilledResponse}
                                  // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
                                  dialogText={dialogText}
                                  onCompletion={this.handleCompletion}
                                  onSaveProgress={this.handleSaveProgress}
                                />
                              ) // non-paginated survey not yet supported
                            }
                          </SubjectIdContext.Provider>
                        </AutoSaveContext.Provider>
                      </IsReadOnlyContext.Provider>
                    </IsEnabledContext.Provider>
                  </SurveyResponseContext.Provider>
                </RepeatingGroupActionsContext.Provider>
              </FieldUpdateContext.Provider>
            </SurveyItemRefsProvider>
          </SurveyContext.Provider>
        </div>
        {showsTemplatePreview && (
          <SurveyTemplatePreview
            response={this._getSurveyResponse()}
            surveyId={survey.id}
            onVisibilityChange={this.handleTemplatePreviewVisibilityChange}
          />
        )}
      </div>
    );
  }
}
const withStateValues =
  <P extends {}>(WrappedComponent: React.ComponentType<P>) =>
  (props: P) => {
    const accountId = useActiveAccountId();
    const policy = useSelector(evaluatedPolicySelectors.selectPolicyForUser);
    return (
      <WrappedComponent {...props} activeAccount={accountId} policy={policy} />
    );
  };

export default withStyles(styles)(withStateValues(SurveyComponent));
